import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface TableActionsState {
  draftActions: {
    isDraftActionSelectionDisabled: boolean;
    isPublishRequestOptionDisabled: boolean;
    hasSelectedDraftRequestsError: boolean;
    isDraftSelectionUnchanged: boolean;
  };
  inboxActions: {
    isInboxActionSelectionDisabled: boolean;
    isSendRequestOptionDisabled: boolean;
    isUndoRequestOptionDisabled: boolean;
    hasSelectedInboxRequestsError: boolean;
    isSelectedInboxRequestsUnchanged: boolean;
  };
  sharedActions: {
    isTableDataChanged: boolean;
  };
}

const initialState: TableActionsState = {
  draftActions: {
    isDraftActionSelectionDisabled: true,
    isPublishRequestOptionDisabled: true,
    isDraftSelectionUnchanged: true,
    hasSelectedDraftRequestsError: false,
  },
  inboxActions: {
    isInboxActionSelectionDisabled: true,
    isSendRequestOptionDisabled: true,
    isUndoRequestOptionDisabled: true,
    hasSelectedInboxRequestsError: false,
    isSelectedInboxRequestsUnchanged: false,
  },
  sharedActions: {
    isTableDataChanged: false,
  },
};

const resourcePlanTableActionsSlice = createSlice({
  name: "resourcePlanTableActions",
  initialState,
  reducers: {
    /**
     * Sets the state of the send request option.
     *
     * @param state The current state of the slice.
     * @param action The state if the send request actions are disabled.
     */
    setIsSendRequestOptionDisabled: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.inboxActions.isSendRequestOptionDisabled = action.payload;
    },
    /**
     * Sets the state of the undo request option.
     *
     * @param state The current state of the slice.
     * @param action The state if the undo request actions are disabled.
     */
    setIsUndoRequestOptionDisabled: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.inboxActions.isUndoRequestOptionDisabled = action.payload;
    },
    /**
     * Sets the state of the inbox action option.
     *
     * @param state The current state of the slice.
     * @param action The state if the inbox actions are disabled.
     */
    setInboxActionDisabled: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.inboxActions.isInboxActionSelectionDisabled = action.payload;
    },
    /**
     * Sets the state of the publish request option for publishing the draft requests.
     *
     * @param state The current state of the slice.
     * @param action The state if the publish request actions are disabled.
     */
    setPublishRequestOptionDisabled: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.draftActions.isPublishRequestOptionDisabled = action.payload;
    },
    /**
     * Sets the state of the draft action option.
     *
     * @param {TableActionsState} state The current state of the slice.
     * @param {PayloadAction<boolean>} action The state if the draft actions are disabled.
     */
    setDraftActionDisabled: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.draftActions.isDraftActionSelectionDisabled = action.payload;
    },

    /**
     * Sets the error state of the selected draft requests. If one of the selected requests is flagged as an error request
     * than this flag should be set and an info shown to warn the user.
     *
     * @param state The current state of the slice.
     * @param action The error state of the selected draft requests.
     */
    setHasSelectedDraftRequestsError: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.draftActions.hasSelectedDraftRequestsError = action.payload;
    },

    /**
     * Sets the change state of the table data. If the table data is changed
     * than this flag should be set to true and an info shown to inform the user.
     *
     * @param state The current state of the slice.
     * @param action The change state of the table data.
     */
    setIsTableDataChanged: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.sharedActions.isTableDataChanged = action.payload;
    },

    /**
     * Sets the error state of the selected inbox requests. If one of the selected requests is flagged as an error
     * than this flag should be set to true and an info shown to warn the user.
     *
     * @param state The current state of the slice.
     * @param action The error state of the selected inbox requests.
     */
    setHasSelectedInboxRequestsError: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.inboxActions.hasSelectedInboxRequestsError = action.payload;
    },

    /**
     * Sets the change state of the selected inbox requests. If the selected request is unchanged
     * than this flag should be set to true and an info shown to inform the user.
     *
     * @param state The current state of the slice.
     * @param action The change state of the selected inbox requests.
     */
    setIsSelectedInboxRequestsUnchanged: (
      state: TableActionsState,
      action: PayloadAction<boolean>
    ) => {
      state.inboxActions.isSelectedInboxRequestsUnchanged = action.payload;
    },

    /**
     * Clears the state of the table actions.
     *
     */
    clearTableActionState: () => initialState,
  },
});

export const {
  setIsSendRequestOptionDisabled,
  setIsUndoRequestOptionDisabled,
  setInboxActionDisabled,
  setPublishRequestOptionDisabled,
  setDraftActionDisabled,
  setHasSelectedDraftRequestsError,
  setHasSelectedInboxRequestsError,
  setIsTableDataChanged,
  clearTableActionState,
  setIsSelectedInboxRequestsUnchanged,
} = resourcePlanTableActionsSlice.actions;

export default resourcePlanTableActionsSlice;
